<script setup lang="ts">
import type { PropType } from 'vue'

const props = defineProps({
  image: {
    type: String,
    default: null,
  },
  imageAlt: {
    type: String,
    default: 'Hero Image',
  },
  imagePosition: {
    type: String,
    default: 'left',
  },
  /** Optional smaller line above the title (e.g. “Hey there! 👋”). */
  intro: {
    type: String,
    default: '',
  },
  /** `object-position` for the hero image (`object-fit: cover`). */
  imageObjectPosition: {
    type: String,
    default: '6% 50%',
  },
  role: {
    type: String,
    default: '',
  },
  roleUrl: {
    type: String,
    default: '',
  },
  previousCompanies: {
    type: Array as PropType<{ name: string, url: string }[]>,
    default: () => [],
  },
  location: {
    type: String,
    default: '',
  },
})

function prevSep(i: number, n: number) {
  if (i >= n - 1) return ''
  if (i === n - 2) return n === 2 ? ' and ' : ', and '
  return ', '
}

function externalHref(url: string) {
  const u = url.trim()
  return u.startsWith('http://') || u.startsWith('https://') ? u : `https://${u}`
}

const roleLinked = computed(() => {
  const r = props.role
  const u = props.roleUrl?.trim()
  if (!r) return null
  if (!u) return { plain: r as string }
  const idx = r.lastIndexOf(' @ ')
  if (idx === -1) return { plain: r }
  return {
    lead: r.slice(0, idx),
    company: r.slice(idx + 3).trim(),
    url: externalHref(u),
  }
})
</script>

<template>
  <section
    class="mb-10 scroll-mt-8 md:mb-14 lg:mb-16"
    aria-labelledby="hero-title"
  >
    <div
      class="relative overflow-hidden rounded-3xl border border-gray-200/80 bg-gradient-to-br from-gray-50 to-slate-100/90 px-6 py-10 sm:px-9 sm:py-11 md:px-11 md:py-12 dark:border-gray-800 dark:from-gray-950 dark:to-slate-950 [background-image:linear-gradient(90deg,rgb(15_23_42_/_0.04)_1px,transparent_0),linear-gradient(rgb(15_23_42_/_0.04)_1px,transparent_0)] [background-size:22px_22px] dark:[background-image:linear-gradient(90deg,rgb(255_255_255_/_0.035)_1px,transparent_0),linear-gradient(rgb(255_255_255_/_0.035)_1px,transparent_0)]"
    >
      <div
        class="relative grid grid-cols-1 items-center gap-10 sm:gap-11 lg:gap-x-12 lg:gap-y-0"
        :class="
          image
            ? imagePosition === 'right'
              ? 'lg:grid-cols-[minmax(0,1fr)_auto]'
              : 'lg:grid-cols-[auto_minmax(0,1fr)]'
            : ''
        "
      >
        <div
          v-if="image"
          class="flex shrink-0 justify-center"
          :class="[
            imagePosition === 'right'
              ? 'lg:col-start-2 lg:row-start-1 lg:justify-end'
              : 'lg:col-start-1 lg:row-start-1 lg:justify-start',
          ]"
        >
          <NuxtImg
            class="h-44 w-44 shrink-0 rounded-full border-2 border-primary-500 object-cover ring-2 ring-white/90 dark:border-primary-400 dark:ring-gray-800/90 sm:h-52 sm:w-52 lg:h-60 lg:w-60"
            :style="{ objectPosition: imageObjectPosition }"
            :src="image"
            :alt="imageAlt"
            format="webp"
            quality="90"
            sizes="xs:176px sm:208px lg:240px"
          />
        </div>

        <div
          class="min-w-0 max-w-2xl text-center lg:text-left"
          :class="
            image
              ? imagePosition === 'right'
                ? 'lg:col-start-1 lg:row-start-1'
                : 'lg:col-start-2 lg:row-start-1'
              : ''
          "
        >
          <p
            v-if="intro"
            class="text-base font-medium text-gray-600 sm:text-lg dark:text-gray-400"
          >
            {{ intro }}
          </p>
          <div
            id="hero-title"
            class="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl dark:text-white"
            :class="intro ? 'mt-0.5' : ''"
          >
            <MDCSlot name="title" unwrap="p">
              Hero title
            </MDCSlot>
          </div>
          <p
            v-if="role"
            class="mt-2 text-base font-medium text-gray-800 dark:text-gray-200 sm:text-lg"
          >
            <template v-if="roleLinked && 'url' in roleLinked && roleLinked.url">
              {{ roleLinked.lead }} @
              <a
                :href="roleLinked.url"
                class="font-semibold text-primary-600 underline decoration-primary-500/70 decoration-2 underline-offset-2 transition-colors hover:text-primary-700 dark:text-primary-400 dark:decoration-primary-400/70 dark:hover:text-primary-300"
                target="_blank"
                rel="noopener noreferrer"
              >{{ roleLinked.company }}</a>
            </template>
            <template v-else>
              {{ role }}
            </template>
          </p>
          <p
            v-if="previousCompanies.length"
            class="mt-1 text-sm text-gray-600 dark:text-gray-400"
          >
            Previously:
            <template
              v-for="(c, i) in previousCompanies"
              :key="c.url + c.name"
            >
              <a
                :href="externalHref(c.url)"
                class="text-inherit underline decoration-primary-500/80 decoration-1 underline-offset-[0.18em] transition-colors hover:text-primary-600 dark:hover:text-primary-400"
                target="_blank"
                rel="noopener noreferrer"
              >{{ c.name }}</a><template v-if="i < previousCompanies.length - 1">{{ prevSep(i, previousCompanies.length) }}</template>
            </template>.
          </p>
          <p
            v-if="location"
            class="mt-1 text-sm text-gray-500 dark:text-gray-400"
          >
            {{ location }}
          </p>
          <div
            class="mt-4 max-w-prose text-base leading-relaxed text-gray-600 dark:text-gray-300 sm:mt-5 sm:text-lg [&_strong]:font-semibold [&_strong]:text-gray-800 dark:[&_strong]:text-gray-100 [&_a]:font-medium [&_a]:text-primary-600 [&_a]:no-underline [&_a]:underline-offset-2 [&_a]:transition-colors hover:[&_a]:text-primary-700 dark:[&_a]:text-primary-400 dark:hover:[&_a]:text-primary-300"
          >
            <MDCSlot name="description" unwrap="p">
              Hero description
            </MDCSlot>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
